import { QRCodeSVG } from "qrcode.react";
import { type FC, type ReactNode, useCallback, useState } from "react";

import { useClipboard } from "../../../hooks/useClipboard";
import {
  hasEnvironmentAdminAccess,
  useOptionalDashboardAuth,
} from "../../../hooks/useDashboardAuth";
import {
  HudActionButton,
  type HudActionTone,
} from "../../common/HudActionButton";
import { type BlackboardInstance } from "../blackboardModels";
import { OverflowActions } from "../environment/EnvironmentLayout";
import {
  getStatusLabel,
  getStatusTone,
  isRestartInFlight,
  liveUsageSummaryLine,
  sandboxSummaryLine,
} from "../environment/environmentUtils";
import {
  RepositoriesBlock,
  formatRefreshResult,
} from "../environment/Repositories";
import { type WorkspaceController } from "../environment/useWorkspaceController";
import { type TeamsBindLink } from "../identity/helpers";
import {
  describeTeamsBindingFailure,
  mintTeamsBindLink,
  unbindTeamsChannel,
} from "../identity/teamsBindingActions";
import {
  type ChannelEnvironment,
  isFailedUnstartedEnvironment,
} from "../useChannelEnvironment";
import { type OrgChannelSummary } from "../useOrgChannels";
import { MemberSummaryRow } from "./MemberSummaryRow";

// ---------------------------------------------------------------------------
// ChannelRail — the shared-channel control rail docked beside the Timeline: an
// accordion of MEMBERS (presence) and WORKSPACE (full environment management).
// Exactly one section is expanded; pressing a header expands it and folds the
// other. The workspace section IS the management surface — there is no
// Workspace tab — so the rail mounts for every shared channel, members or not
// (a memberless channel with a retained environment is a normal lifecycle
// state, and attach must stay reachable on empty channels). The active section
// is owned by ChannelSheet so it survives rail hide/show and tab switches.
// Every mutation flows through the same modals as before (WorkspaceDialogs,
// rendered once at sheet level), so the rail stays a narrow column of truth
// with the heavy interactions in overlays.
// ---------------------------------------------------------------------------

const TONE_TEXT_CLASS: Record<string, string> = {
  danger: "text-rose-600 dark:text-rose-400",
  info: "text-sky-600 dark:text-sky-400",
  neutral: "text-slate-500 dark:text-slate-400",
  success: "text-emerald-600 dark:text-emerald-400",
  warning: "text-amber-600 dark:text-amber-400",
};

export type RailSection = "connections" | "members" | "workspace";

interface ChannelRailProps {
  members: BlackboardInstance[];
  /** Teams binding state for this channel, read straight off the org channel
   *  summary the sheet already holds -- no extra request. Null when the summary
   *  has not loaded. */
  channel?: OrgChannelSummary | null;
  /** Whether the org has the Teams chat SDK enabled. The section is omitted
   *  entirely when it is off: a connection surface for a product the org has
   *  not connected is noise, not information. */
  teamsEnabled?: boolean;
  /** Both Teams mutations are org-admin only; without this the section would
   *  offer buttons that answer 403. */
  teamsIsAdmin?: boolean;
  /** Whether this deployment has an active Teams bot registration. The org flag
   *  being on is not enough: without a live bot, bind mints a link nothing will
   *  ever answer, which is exactly how a bench deployment sent an operator into
   *  Teams chasing a card that could not be posted. */
  teamsBotReady?: boolean;
  /** The unbind endpoint publishes no realtime event, so the summary this
   *  section renders will not refresh itself. Without a reload the rail would
   *  keep claiming "bound" after a successful unbind -- the same staleness the
   *  Identity tab papers over with a local set of unbound ids. */
  onReloadChannels?: () => Promise<boolean> | void;
  killingInstanceId: string | null;
  activeSection: RailSection;
  onSectionChange: (section: RailSection) => void;
  onInspectActor: (instanceId: string) => void;
  onKill: (instanceId: string) => void;
  onLifecycleChange?: () => void;
  workspace: WorkspaceController;
}

export const ChannelRail: FC<ChannelRailProps> = ({
  members,
  channel = null,
  teamsEnabled = false,
  teamsIsAdmin = false,
  teamsBotReady = false,
  onReloadChannels,
  killingInstanceId,
  activeSection,
  onSectionChange,
  onInspectActor,
  onKill,
  onLifecycleChange,
  workspace,
}) => {
  const { environment } = workspace;
  /* An org that loses the Teams flag -- or a switch to an org without it --
     must not leave the rail showing a header-less void, so a stale
     "connections" selection falls back to the section that always exists. */
  const section =
    activeSection === "connections" && !teamsEnabled
      ? "workspace"
      : activeSection;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <RailSectionHeader
        label="members"
        meta={String(members.length)}
        active={section === "members"}
        onActivate={() => onSectionChange("members")}
      />
      {section === "members" && (
        <div className="native-scrollbar min-h-0 flex-1 space-y-px overflow-y-auto overflow-x-hidden p-1.5">
          {members.length === 0 && (
            <p className="p-3 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-600">
              no active members
            </p>
          )}
          {members.map((member) => (
            <MemberSummaryRow
              key={member.id}
              member={member}
              killing={killingInstanceId === member.id}
              onInspectActor={onInspectActor}
              onKill={onKill}
              onLifecycleChange={onLifecycleChange}
              variant="rail"
            />
          ))}
        </div>
      )}
      <RailSectionHeader
        label="workspace"
        meta={environment === null ? "none" : getStatusLabel(environment)}
        metaTone={TONE_TEXT_CLASS[getStatusTone(environment?.status ?? null)]}
        active={section === "workspace"}
        onActivate={() => onSectionChange("workspace")}
      />
      {section === "workspace" && (
        <div className="native-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-2.5">
          <RailWorkspaceBody workspace={workspace} />
        </div>
      )}
      {teamsEnabled && (
        <>
          <RailSectionHeader
            label="connections"
            meta={connectionsMeta(channel)}
            metaTone={connectionsMetaTone(channel)}
            active={section === "connections"}
            onActivate={() => onSectionChange("connections")}
          />
          {section === "connections" && (
            <div className="native-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-2.5">
              <RailConnectionsBody
                channel={channel}
                isAdmin={teamsIsAdmin}
                botReady={teamsBotReady}
                onReloadChannels={onReloadChannels}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

/** Accordion section header: mono micro-label + a chevron that rotates to
 *  point down when the section is the active one. */
const RailSectionHeader: FC<{
  label: string;
  meta?: string;
  metaTone?: string;
  active: boolean;
  onActivate: () => void;
}> = ({ label, meta, metaTone, active, onActivate }) => (
  <button
    type="button"
    onClick={onActivate}
    aria-expanded={active}
    className="flex shrink-0 items-center gap-1.5 border-b border-t border-slate-200 px-3 py-2 text-left transition-colors first:border-t-0 hover:bg-slate-100/60 dark:border-slate-800 dark:hover:bg-slate-800/40"
  >
    <span
      aria-hidden
      className={`inline-block font-mono text-[9px] text-slate-400 transition-transform duration-150 dark:text-slate-500 ${
        active ? "rotate-90" : ""
      }`}
    >
      ▸
    </span>
    <span
      className={`font-mono text-[9px] font-bold uppercase tracking-[0.25em] ${
        active
          ? "text-slate-600 dark:text-slate-300"
          : "text-slate-400 dark:text-slate-500"
      }`}
    >
      [ {label} ]
    </span>
    {meta && (
      <span
        className={`ml-auto font-mono text-[9px] uppercase tracking-wider ${
          metaTone ?? "text-slate-400 dark:text-slate-500"
        }`}
      >
        {meta}
      </span>
    )}
  </button>
);

// --- workspace management body -------------------------------------------------

/** Exported for tests — rendered inside ChannelRail's workspace section. */
export const RailWorkspaceBody: FC<{ workspace: WorkspaceController }> = ({
  workspace,
}) => {
  const {
    actionError,
    busy,
    clearActionError,
    environment,
    envRetryable,
    error: fetchError,
    launchPreview,
    loading,
    openDialog,
    processToolsAvailable,
    refresh,
    refreshRepositories,
    refreshResult,
    repositories,
    repositoriesError,
    runAction,
    start,
    statusTone,
  } = workspace;
  const auth = useOptionalDashboardAuth();
  // Archive / kill / remove are admin-level lifecycle operations (they destroy
  // or stop the shared workspace); members keep attach/edit/restart/repos.
  // Exception mirrored from the server: an errored env may be discarded.
  const isEnvironmentAdmin = auth !== null && hasEnvironmentAdminAccess(auth);

  return (
    <div className="space-y-3">
      {fetchError && <RailNotice tone="danger">{fetchError}</RailNotice>}
      {actionError && (
        <RailNotice tone="warn" onDismiss={clearActionError}>
          {actionError}
        </RailNotice>
      )}
      {refreshResult && (
        <RailNotice tone="info">
          {formatRefreshResult(refreshResult)}
        </RailNotice>
      )}
      {repositoriesError && (
        <RailNotice tone="danger">repos: {repositoriesError}</RailNotice>
      )}

      {environment === null ? (
        <>
          <p className="font-mono text-[10px] leading-relaxed text-slate-500 dark:text-slate-500">
            {loading ? "loading…" : "no environment attached"}
          </p>
          {!loading && (
            <>
              <div className="space-y-1 font-mono text-[9px] leading-relaxed text-slate-400 dark:text-slate-600">
                <p>process tools · spawn / exec / file i/o</p>
                <p>isolation · per-channel container + volume</p>
                <p>resource caps · ram / cpu / wall governed</p>
              </div>
              <RailChip
                tone="success"
                onClick={() => openDialog({ kind: "attach" })}
              >
                + attach environment
              </RailChip>
            </>
          )}
        </>
      ) : (
        <>
          <RailStat label="status">
            <span className="flex items-center gap-1.5">
              <span
                className={
                  TONE_TEXT_CLASS[statusTone] ?? TONE_TEXT_CLASS.neutral
                }
              >
                {getStatusLabel(environment)}
              </span>
              {isRestartInFlight(environment) && (
                <span className="text-amber-500">· restarting</span>
              )}
            </span>
            {/* Removing rows carry the interrupted-remove manual-recovery
                detail — surfacing it here is the operator's only signpost, so
                error detail renders for both terminal-error and removing. */}
            {(environment.status === "errored" ||
              environment.status === "removing") &&
              environment.lastErrorMessage && (
                <span className="mt-1 block break-words text-rose-500 dark:text-rose-400">
                  {environment.lastErrorMessage}
                </span>
              )}
          </RailStat>

          <div className="flex flex-wrap items-center gap-1.5">
            {/* Hibernated is lossless + resumable: give it a first-class
                "resume" (start) rather than the generic "restart" the
                non-retryable branch would otherwise show. */}
            {environment.status === "hibernated" && (
              <RailChip
                tone="success"
                busy={busy === "resume"}
                onClick={() =>
                  void runAction("resume", start, { autoClose: false })
                }
              >
                resume
              </RailChip>
            )}
            {environment.status !== "hibernated" && envRetryable && (
              <RailChip
                tone="success"
                busy={busy === "start"}
                onClick={() =>
                  void runAction("start", start, { autoClose: false })
                }
              >
                retry start
              </RailChip>
            )}
            {/* Deleted is terminal in the rail: no restart/edit — the row
                only reads out retained state until purge. */}
            {environment.status !== "hibernated" &&
              environment.status !== "deleted" &&
              !envRetryable && (
                <RailChip
                  onClick={() => openDialog({ kind: "restart-confirm" })}
                >
                  restart
                </RailChip>
              )}
            {environment.status !== "deleted" && (
              <RailChip onClick={() => openDialog({ kind: "edit" })}>
                edit
              </RailChip>
            )}
            <RailChip
              busy={busy === "refresh"}
              onClick={() =>
                void runAction("refresh", async () => {
                  await refresh();
                  await refreshRepositories();
                })
              }
            >
              refresh
            </RailChip>
            {(isEnvironmentAdmin ||
              isFailedUnstartedEnvironment(environment)) && (
              <OverflowActions
                environment={environment}
                adminAccess={isEnvironmentAdmin}
                compact
                onArchive={() => openDialog({ kind: "archive-confirm" })}
                onDetach={() => openDialog({ kind: "detach-confirm" })}
              />
            )}
          </div>

          <RailStat label="process tools">
            <span
              className={
                processToolsAvailable
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-slate-400 dark:text-slate-600"
              }
              title="Process tools reach actors only when the environment is running and the grant is on."
            >
              {processToolsAvailable ? "on" : "off"}
            </span>
          </RailStat>

          <RailStat label="sandbox" title={environment.containerImage}>
            {sandboxSummaryLine(environment)}
          </RailStat>

          <RailStat label="live usage">
            {liveUsageSummaryLine(environment)}
          </RailStat>

          {environment.reservationHeld !== null && (
            <RailReservationMeter held={environment.reservationHeld} />
          )}

          <RailStat label="preview">
            {environment.previewPorts.length === 0 ? (
              "no ports"
            ) : (
              <span className="space-y-1">
                {environment.previewPorts.map((port) => (
                  <span
                    key={port.publicPort}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="truncate">
                      {port.label ?? "port"} :{port.containerPort}
                    </span>
                    <RailChip
                      busy={busy === `preview-${port.publicPort}`}
                      onClick={() =>
                        void runAction(
                          `preview-${port.publicPort}`,
                          async () => {
                            const url = await launchPreview(port.publicPort);
                            window.open(url, "_blank", "noopener,noreferrer");
                          },
                          { autoClose: false },
                        )
                      }
                    >
                      open
                    </RailChip>
                  </span>
                ))}
              </span>
            )}
          </RailStat>

          {/* The one repository lifecycle renderer. The rail used to hand-roll
              its own rows, which silently dropped the blocker reason, the retry
              guidance, and every tombstone's identity on the surface operators
              actually use. */}
          <RepositoriesBlock
            compact
            environment={environment}
            repositories={repositories}
            onAcknowledgeAbsentDetach={(repository) =>
              openDialog({ kind: "acknowledge-absent-detach", repository })
            }
            onAttach={() => openDialog({ kind: "attach-repo" })}
            onChangeBranch={(repository) =>
              openDialog({ kind: "change-branch", repository })
            }
            onDetach={(repository) =>
              openDialog({ kind: "detach-repo", repository })
            }
            onRefresh={(repository) =>
              openDialog({ kind: "refresh-repo", repository })
            }
            onRestart={() => openDialog({ kind: "restart-confirm" })}
          />
        </>
      )}
    </div>
  );
};

// --- primitives -----------------------------------------------------------------

/** Meta for the collapsed CONNECTIONS header. "none" reads as a fact rather
 *  than a fault: most channels are not bound, and an unbound channel is not a
 *  problem to fix. A detached binding is called out because it is the one state
 *  that needs a human -- the Teams side went away while the channel kept the
 *  hold. */
export function connectionsMeta(channel: OrgChannelSummary | null): string {
  if (channel === null) {
    return "\u2014";
  }
  if (channel.activeTeamsBindingId !== null) {
    return "teams";
  }
  return channel.hasDetachedTeamsBinding ? "detached" : "none";
}

function connectionsMetaTone(channel: OrgChannelSummary | null): string {
  if (channel === null) {
    return "text-slate-400 dark:text-slate-500";
  }
  if (channel.activeTeamsBindingId !== null) {
    return "text-cyan-600 dark:text-cyan-400";
  }
  return channel.hasDetachedTeamsBinding
    ? "text-amber-600 dark:text-amber-400"
    : "text-slate-400 dark:text-slate-500";
}

/** The channel-scoped Teams surface. Bind and unbind used to live only in an
 *  actor's Identity tab, which meant channel state was managed from an actor
 *  and the channel view could not even report whether it was mirrored.
 *
 *  Compact by construction: actions are the rail's own chips, the minted link
 *  collapses to a single copyable line with the QR behind a toggle, and every
 *  label is one word. Nothing here is hover-gated -- the rail contract requires
 *  state to be readable without pointing at it. */
export const RailConnectionsBody: FC<{
  channel: OrgChannelSummary | null;
  isAdmin?: boolean;
  botReady?: boolean;
  onReloadChannels?: () => Promise<boolean> | void;
}> = ({ channel, isAdmin = false, botReady = false, onReloadChannels }) => {
  const [link, setLink] = useState<TeamsBindLink | null>(null);
  const [busy, setBusy] = useState<"bind" | "unbind" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingUnbind, setConfirmingUnbind] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const channelId = channel?.id ?? null;

  const handleBind = useCallback(async () => {
    if (channelId === null) {
      return;
    }
    setBusy("bind");
    setError(null);
    try {
      const result = await mintTeamsBindLink(channelId);
      if (result.ok) {
        setLink(result.link);
      } else {
        setError(describeTeamsBindingFailure(result.failure));
      }
    } catch {
      setError("Could not reach the server.");
    } finally {
      setBusy(null);
    }
  }, [channelId]);

  const handleUnbind = useCallback(async () => {
    if (channelId === null) {
      return;
    }
    setBusy("unbind");
    setError(null);
    try {
      const result = await unbindTeamsChannel(channelId);
      if (result.ok) {
        setLink(null);
        setConfirmingUnbind(false);
        // The endpoint fires no realtime event, so ask for the summary again;
        // otherwise this panel keeps rendering the binding it just removed.
        await onReloadChannels?.();
      } else {
        setError(describeTeamsBindingFailure(result.failure));
      }
    } catch {
      setError("Could not reach the server.");
    } finally {
      setBusy(null);
    }
  }, [channelId, onReloadChannels]);

  if (channel === null) {
    return (
      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-600">
        loading
      </p>
    );
  }

  const bound = channel.activeTeamsBindingId !== null;

  return (
    <div className="space-y-2.5">
      <RailStat label="teams">
        <span
          className={
            bound
              ? "text-cyan-700 dark:text-cyan-300"
              : "text-slate-400 dark:text-slate-500"
          }
        >
          {bound ? "bound" : "not bound"}
        </span>
      </RailStat>
      {bound && (
        <RailStat
          label="conversation"
          title={channel.activeTeamsConversationId ?? undefined}
        >
          {channel.activeTeamsConversationLabel ?? "unnamed conversation"}
        </RailStat>
      )}
      {bound && channel.teamsBindingChannelKind !== null && (
        <RailStat label="mode">
          {channel.teamsBindingChannelKind === "shared_bound"
            ? "shared + teams"
            : "teams only"}
        </RailStat>
      )}
      {!bound && channel.hasDetachedTeamsBinding && (
        <p className="border-l-2 border-amber-500/40 pl-2 text-[10px] leading-snug text-slate-500 dark:text-slate-400">
          A previous binding was detached. Messages no longer mirror.
        </p>
      )}

      <RailConnectionsActions
        bound={bound}
        isAdmin={isAdmin}
        botReady={botReady}
        busy={busy}
        hasLink={link !== null}
        confirming={confirmingUnbind}
        onRequestUnbind={() => setConfirmingUnbind(true)}
        onCancelUnbind={() => setConfirmingUnbind(false)}
        onBind={() => void handleBind()}
        onUnbind={() => void handleUnbind()}
      />

      {error !== null && (
        <p className="border-l-2 border-rose-500/50 pl-2 text-[10px] leading-snug text-rose-600 dark:text-rose-400">
          {error}
        </p>
      )}

      {link !== null && !bound && (
        <RailBindLink
          link={link}
          showQr={showQr}
          onToggleQr={() => setShowQr(!showQr)}
          onDismiss={() => {
            setLink(null);
            setShowQr(false);
          }}
        />
      )}
    </div>
  );
};

/** Split out of the body so each lifecycle branch is a plain early return:
 *  bound/confirming/unbound as nested ternaries tripped no-nested-ternary, and
 *  the rule is right -- three states reading top-to-bottom is easier to audit
 *  than one expression. */
const RailConnectionsActions: FC<{
  bound: boolean;
  isAdmin: boolean;
  botReady: boolean;
  busy: "bind" | "unbind" | null;
  hasLink: boolean;
  confirming: boolean;
  onRequestUnbind: () => void;
  onCancelUnbind: () => void;
  onBind: () => void;
  onUnbind: () => void;
}> = ({
  bound,
  isAdmin,
  botReady,
  busy,
  hasLink,
  confirming,
  onRequestUnbind,
  onCancelUnbind,
  onBind,
  onUnbind,
}) => {
  if (!botReady) {
    /* Reported before the admin check: when no bot is live the restriction that
       matters is the deployment's, and telling an admin "you may do this" would
       be worse than telling them it cannot be done yet. An existing binding
       still renders its state above -- only the actions are withheld. */
    return (
      <p className="text-[10px] leading-snug text-slate-500 dark:text-slate-400">
        Teams is not connected for this deployment.
      </p>
    );
  }
  if (!isAdmin) {
    return (
      <p className="text-[10px] leading-snug text-slate-500 dark:text-slate-400">
        Only an org admin can change this.
      </p>
    );
  }
  if (!bound) {
    return (
      <div className="flex flex-wrap gap-1.5">
        <RailChip
          tone="success"
          busy={busy === "bind"}
          title="Mint a link that binds this channel to a Teams conversation"
          onClick={onBind}
        >
          {hasLink ? "new link" : "bind"}
        </RailChip>
      </div>
    );
  }
  if (confirming) {
    return (
      <div className="flex flex-wrap gap-1.5">
        <RailChip tone="danger" busy={busy === "unbind"} onClick={onUnbind}>
          confirm
        </RailChip>
        <RailChip onClick={onCancelUnbind}>cancel</RailChip>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {/* Two steps, unlike the Identity tab's single click: unbinding stops
          mirroring for everyone in the Teams conversation, which is too much to
          spend on one mis-click in a dense panel. */}
      <RailChip
        tone="danger"
        title="Stop mirroring this channel into Teams"
        onClick={onRequestUnbind}
      >
        unbind
      </RailChip>
    </div>
  );
};

/** The minted link, rail-sized. The full-width panel spends a 72px QR, two
 *  buttons, a minted line and two teaching paragraphs on this; at 240px that
 *  would be a wall, so the URL becomes one selectable line with copy beside it
 *  and the QR is opt-in for the phone-scanning case. */
const RailBindLink: FC<{
  link: TeamsBindLink;
  showQr: boolean;
  onToggleQr: () => void;
  onDismiss: () => void;
}> = ({ link, showQr, onToggleQr, onDismiss }) => {
  const { copy, status } = useClipboard({ resetDelay: 1500 });
  const expires =
    link.expiresAt === null
      ? null
      : new Date(link.expiresAt).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });

  return (
    <div className="space-y-1.5 border-l-2 border-cyan-500/40 pl-2">
      <p className="font-mono text-[8px] font-bold uppercase tracking-[0.25em] text-slate-500 dark:text-slate-400">
        bind link
      </p>
      <div className="flex min-w-0 items-center gap-1">
        <input
          aria-label="Teams binding link"
          readOnly
          value={link.url}
          onFocus={(event) => event.currentTarget.select()}
          className="min-w-0 flex-1 border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] text-slate-600 outline-none focus:border-cyan-500/50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300"
        />
        <RailChip
          tone={status === "copied" ? "success" : "neutral"}
          onClick={() => void copy(link.url)}
        >
          {status === "copied" ? "copied" : "copy"}
        </RailChip>
      </div>
      <p className="text-[10px] leading-snug text-slate-500 dark:text-slate-400">
        Send it into a Teams chat — the bot replies with a bind card.
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        <a
          href={link.url}
          rel="noreferrer"
          target="_blank"
          className="border border-cyan-500/40 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-cyan-700 transition-colors hover:border-cyan-500 dark:border-cyan-400/30 dark:text-cyan-300"
        >
          open
        </a>
        <RailChip onClick={onToggleQr}>{showQr ? "hide qr" : "qr"}</RailChip>
        <RailChip onClick={onDismiss}>dismiss</RailChip>
        {expires !== null && (
          <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500">
            expires {expires}
          </span>
        )}
      </div>
      {showQr && (
        <div className="flex w-fit items-center justify-center border border-cyan-400/25 bg-white p-1">
          <QRCodeSVG
            value={link.url}
            size={104}
            bgColor="#ffffff"
            fgColor="#020617"
            level="M"
          />
        </div>
      )}
    </div>
  );
};

const RailStat: FC<{ label: string; children: ReactNode; title?: string }> = ({
  label,
  children,
  title,
}) => (
  <div className="min-w-0">
    <p className="font-mono text-[8px] font-bold uppercase tracking-[0.25em] text-slate-400 dark:text-slate-600">
      {label}
    </p>
    {/* `break-words` is the backstop for values the rail cannot predict: image
        references, digests and ids are single unbreakable tokens, and without it
        one of them drags the whole pane into a horizontal scroll. */}
    <div
      className="mt-0.5 break-words font-mono text-[10px] leading-relaxed text-slate-600 dark:text-slate-400"
      title={title}
    >
      {children}
    </div>
  </div>
);

/** Reservation meter — makes the hibernation model legible: what compute is
 *  reserved (or freed at rest) and how the held disk breaks down by segment.
 *  Renders only when the environment holds a live resource reservation. */
const RailReservationMeter: FC<{
  held: NonNullable<ChannelEnvironment["reservationHeld"]>;
}> = ({ held }) => {
  // Segments are disjoint (workspace/rootfs bases exclude their sub-parts) and
  // sum to held.totalMb — the read model guarantees this, so we trust totalMb
  // rather than re-summing.
  const segments = [
    {
      class: "bg-emerald-500 dark:bg-emerald-400",
      label: "ws",
      mb: held.workspaceBaseMb,
    },
    {
      class: "bg-sky-500 dark:bg-sky-400",
      label: "rootfs",
      mb: held.rootfsBaseMb,
    },
    {
      class: "bg-slate-400 dark:bg-slate-500",
      label: "rt",
      mb: held.runtimeMb,
    },
    {
      class: "bg-violet-500 dark:bg-violet-400",
      label: "archive",
      mb: held.archiveMb,
    },
    {
      class: "bg-amber-500 dark:bg-amber-400",
      label: "image",
      mb: held.imageMb,
    },
  ];
  const total = held.totalMb;
  const shown = segments.filter((segment) => segment.mb > 0);
  const computeFreed = held.computeReleasedAt !== null;
  return (
    <div className="space-y-2">
      <RailStat label="compute">
        <span
          className={
            computeFreed
              ? "text-emerald-600 dark:text-emerald-400"
              : "text-slate-600 dark:text-slate-400"
          }
        >
          {computeFreed ? "freed at rest" : "reserved live"}
          <span className="text-slate-400 dark:text-slate-600">
            {" · "}
            {held.requestedCpuMilli}m cpu · {held.requestedRamMb} MiB ram
          </span>
        </span>
      </RailStat>
      <div>
        <p className="font-mono text-[8px] font-bold uppercase tracking-[0.25em] text-slate-400 dark:text-slate-600">
          storage held · {total} MiB
        </p>
        {total > 0 ? (
          <>
            <div className="mt-1 flex h-1.5 w-full overflow-hidden">
              {shown.map((segment) => (
                <div
                  key={segment.label}
                  className={segment.class}
                  style={{ width: `${(segment.mb / total) * 100}%` }}
                  title={`${segment.label} ${segment.mb} MiB`}
                />
              ))}
            </div>
            <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 font-mono text-[8px] tabular-nums text-slate-400 dark:text-slate-500">
              {shown.map((segment) => (
                <span
                  key={segment.label}
                  className="inline-flex items-center gap-1"
                >
                  <span
                    aria-hidden
                    className={`inline-block h-1.5 w-1.5 ${segment.class}`}
                  />
                  {segment.label} {segment.mb}
                </span>
              ))}
            </div>
          </>
        ) : (
          <p className="mt-0.5 font-mono text-[10px] text-slate-400 dark:text-slate-600">
            none held
          </p>
        )}
      </div>
    </div>
  );
};

/** Rail action. A thin alias over the shared HudActionButton at `sm`: the rail
 *  used to carry its own copy of the button styling, which is how "restart" and
 *  "more" drifted apart -- different padding, font size, text colour and hover
 *  treatment, plus a scale animation on one and not the other. Keeping a single
 *  implementation means the next tone or state added shows up on every rail
 *  action at once. `children` stays a string for call-site readability. */
const RailChip: FC<{
  children: string;
  onClick: () => void;
  tone?: HudActionTone;
  busy?: boolean;
  title?: string;
}> = ({ children, onClick, tone = "neutral", busy = false, title }) => (
  <HudActionButton
    label={children}
    loading={busy}
    onClick={onClick}
    size="sm"
    title={title}
    tone={tone}
  />
);

const RailNotice: FC<{
  children: ReactNode;
  tone: "danger" | "warn" | "info";
  onDismiss?: () => void;
}> = ({ children, tone, onDismiss }) => {
  const toneClass = {
    danger: "border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-300",
    info: "border-sky-500/30 bg-sky-500/5 text-sky-800 dark:text-sky-200",
    warn: "border-amber-500/40 bg-amber-500/5 text-amber-800 dark:text-amber-200",
  }[tone];
  return (
    <div
      className={`flex items-start justify-between gap-2 border px-2 py-1.5 font-mono text-[9px] leading-relaxed ${toneClass}`}
    >
      <span className="min-w-0 break-words">{children}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 uppercase tracking-widest opacity-70 hover:opacity-100"
        >
          ×
        </button>
      )}
    </div>
  );
};
